import { useCallback, useState } from 'react';

import {
  CANCELLED,
  DragId,
  DragItem,
  DropItem,
  KeyboardInteraction,
  MOVE_LEFT,
  MOVE_RIGHT,
  PICKUP,
  PLACED,
} from '../../../shared/interactive-drag-drop/interactive-drag-drop.constants';
import {
  addItemToDropZone,
  findDropItemByPreviewId,
  isEveryDropAreaOccupied,
  isEveryItemDropped,
  markDragItemAsDropped,
  moveToFreePosition,
  removeItemFromDropZone,
} from '../../../shared/interactive-drag-drop/interactive-drag-drop.utils';
import {
  checkAnswer,
  checkAnswers,
  isEverythingCorrect,
} from '../../shared/interactive-response-shared.utils';

export const useInteractiveResponsePlay = ({
  isCheckAnswerAtEnd,
  isFreeResponse,
  playClick,
  onAnswerAttempt,
}: {
  isCheckAnswerAtEnd: boolean;
  isFreeResponse?: boolean;
  playClick: () => void;
  onAnswerAttempt: (dropItems: DropItem[], dragItems: DragItem[]) => void;
}) => {
  const [dropItems, setDropItems] = useState<DropItem[]>([]);
  const [dragItems, setDragItems] = useState<DragItem[]>([]);
  const [lastDropLocation, setLastDropLocation] = useState<number>(-1);
  const [keyboardState, setKeyboardState] = useState<KeyboardInteraction>();
  const [playAttempt, setPlayAttempt] = useState<number>(1);

  const handleOnDrop = useCallback(
    (destinationId: DragId, sourceId: DragId) => {
      const dropItem = dropItems.find(item => item.id === destinationId);
      const dragItem = dragItems.find(item => item.id === sourceId);

      if (dragItem && dropItem) {
        if (!dragItem.isDropped) {
          // move to drop area
          addItemToDropZone(dropItem, dragItem);
          setDropItems([...dropItems]);
          dragItem.isDropped = true;
          setDragItems([...dragItems]);

          const isEverythingAnswered = isEveryItemDropped(dropItems);

          if (!isFreeResponse && !isCheckAnswerAtEnd) {
            checkAnswers(dropItems, dragItems);
            setDropItems([...dropItems]);
            if (isEverythingCorrect(dropItems, dragItems)) {
              onAnswerAttempt(dropItems, dragItems);
            }
          }

          if (isFreeResponse && isEverythingAnswered) {
            onAnswerAttempt(dropItems, dragItems);
          }
        } else {
          removeItemFromDropZone(dropItems, sourceId);
          addItemToDropZone(dropItem, dragItem);
          setDropItems(() => [...dropItems]);
        }
      }
      playClick();
    },
    [
      dropItems,
      dragItems,
      isCheckAnswerAtEnd,
      isFreeResponse,
      playClick,
      onAnswerAttempt,
    ]
  );

  const handleItemPickUp = useCallback(
    (interaction: KeyboardInteraction) => {
      setLastDropLocation(
        dropItems.findIndex(item =>
          item.zones.find(l => l.dragItemId === interaction.id)
        )
      );
      setKeyboardState(interaction);
    },
    [dropItems]
  );

  const handleItemCancelled = useCallback(
    (interaction: KeyboardInteraction) => {
      if (lastDropLocation === -1) {
        removeItemFromDropZone(dropItems, interaction.id);
        const dragIdx = dragItems.findIndex(item => item.id === interaction.id);
        dragItems[dragIdx].isDropped = false;
        setDragItems([...dragItems]);
      } else {
        const location = dropItems[lastDropLocation].zones.find(
          z => z.previewItemId === interaction.id
        );
        if (location) {
          location.dragItemId = interaction.id;
        }
        setDropItems([...dropItems]);
      }
      setKeyboardState(interaction);
    },
    [dropItems, dragItems, lastDropLocation]
  );

  const handleItemPlacedDown = useCallback(
    (interaction: KeyboardInteraction) => {
      const dropItem = findDropItemByPreviewId(dropItems, interaction.id);
      if (dropItem) {
        const zone = dropItem.zones.find(
          z => z.previewItemId === interaction.id
        );
        zone!.previewItemId = null;
        zone!.dragItemId = interaction.id;
        setDropItems([...dropItems]);
        interaction.announce = `${interaction.announce} ${dropItem.label}`;
        setKeyboardState({ ...interaction });
        playClick();

        if (!isFreeResponse && !isCheckAnswerAtEnd) {
          const dragItem = dragItems.find(d => d.id === interaction.id);
          if (dragItem) checkAnswer(dropItem, dragItem);
          setDropItems([...dropItems]);
        }

        const isEverythingAnswered = isEveryItemDropped(dropItems);
        if (isFreeResponse && isEverythingAnswered) {
          onAnswerAttempt(dropItems, dragItems);
        }
      } else {
        return;
      }
    },
    [
      dropItems,
      dragItems,
      playClick,
      isCheckAnswerAtEnd,
      isFreeResponse,
      onAnswerAttempt,
    ]
  );

  const handleItemMove = useCallback(
    (interaction: KeyboardInteraction) => {
      if (isEveryDropAreaOccupied(dropItems)) {
        return;
      }
      const { dropItems: updatedItems, movedTo } = moveToFreePosition(
        interaction.id,
        dropItems,
        interaction.action === MOVE_LEFT ? 'left' : 'right'
      );
      setDropItems([...updatedItems]);
      setDragItems([...markDragItemAsDropped(interaction.id, dragItems)]);

      interaction.announce = `${interaction.announce} ${dropItems[movedTo].label}`;
      setKeyboardState(interaction);
    },
    [dropItems, dragItems]
  );

  const handleKeyboardInteraction = useCallback(
    (interaction: KeyboardInteraction) => {
      if (interaction.action === PICKUP) {
        handleItemPickUp(interaction);
      }

      if (interaction.action === CANCELLED) {
        handleItemCancelled(interaction);
      }

      if (interaction.action === PLACED) {
        handleItemPlacedDown(interaction);
      }

      if (
        interaction.action === MOVE_LEFT ||
        interaction.action === MOVE_RIGHT
      ) {
        handleItemMove(interaction);
      }
    },
    [
      handleItemPickUp,
      handleItemCancelled,
      handleItemPlacedDown,
      handleItemMove,
    ]
  );

  const handleCheckAnswers = useCallback(() => {
    const updatedDropItems = checkAnswers(dropItems, dragItems);
    setDropItems(() => [...updatedDropItems]);
    onAnswerAttempt(updatedDropItems, dragItems);
  }, [dropItems, dragItems, setDropItems, onAnswerAttempt]);

  return {
    playAttempt,
    setPlayAttempt,
    dropItems,
    keyboardState,
    dragItems,
    setDropItems,
    setDragItems,
    setKeyboardState,
    handleItemPickUp,
    handleOnDrop,
    handleKeyboardInteraction,
    handleCheckAnswers,
  };
};
